import { NavigationType } from 'react-router'
import type { Location, Path, RouterInit, To } from 'react-router'

import { isRouteLocale } from './is-route-locale'

/** react-router does not export `History`, but its router init is typed with it. */
export type History = RouterInit['history']

type HistoryListener = Parameters<History['listen']>[0]

/**
 * A `History` that hides the URL locale prefix from the router.
 *
 * The router is given locale-free routes and only ever sees locale-free
 * locations, so `/models/audio/lyria-3` and `/de/models/audio/lyria-3` are the
 * same location to it — the same match, the same route ids, the same component
 * identity. The prefix is reapplied on the way out: in `createHref` so links
 * and crawlers see the localized URL, and in `push`/`replace` so the address bar
 * does.
 *
 * This replaces a `/:locale?` route parent. A parent segment that matches any
 * string competes with every real route for the first path segment, and
 * react-router 8 removed the regexp constraints that would have let us pin it to
 * shipped locales — which is how `/models/audio/lyria-3` came to be matched with
 * `models` as the locale.
 *
 * `setLocaleSegment` is what makes this viable where `basename` was not.
 * `basename` is fixed when the router is created, so changing language would
 * need a new router and remount the tree. Here the locale is adapter state, the
 * router instance never changes, and the rewrite never reaches the router.
 */
export interface LocaleHistory extends History {
  /** The locale segment on the address bar, or `undefined` for the unprefixed default language. */
  readonly localeSegment: string | undefined
  /**
   * Rewrites the address bar to carry `segment`, without navigating.
   *
   * The router is never notified, because the location it cares about has not
   * changed: no route rematches, no loader re-runs, no component unmounts, and
   * the history entry keeps its index so back and forward still work. Search,
   * hash and history state are preserved.
   */
  setLocaleSegment(segment: string | undefined): void
  /**
   * Re-runs the English→localized address-bar rewrite. Needed when the slug
   * table arrives after history is created (nested URLs have no prerender
   * inject).
   */
  refreshLocalizedSlug(): void
}

interface SplitPathname {
  localeSegment: string | undefined
  pathname: string
}

/** Splits a leading locale segment off a pathname. */
export function splitLocaleSegment(pathname: string): SplitPathname {
  const [, first = '', ...rest] = pathname.split('/')

  if (!isRouteLocale(first)) {
    return { localeSegment: undefined, pathname }
  }

  const remainder = rest.join('/')

  return { localeSegment: first, pathname: remainder === '' ? '/' : `/${remainder}` }
}

function toPath(to: To): Partial<Path> {
  return typeof to === 'string' ? { pathname: to } : to
}

export interface SlugTranslator {
  toCanonical(pathname: string, segment: string): string
  toLocalized(pathname: string, segment: string): string
}

const passThroughSlugs: SlugTranslator = {
  toCanonical: pathname => pathname,
  toLocalized: pathname => pathname,
}

interface CreateLocaleHistoryOptions {
  window?: Window
  slugs?: SlugTranslator
  /**
   * Slug-table segment for URLs without a locale prefix — the host app's
   * default language. Without it, unprefixed paths pass through untranslated,
   * so a bare URL carrying another locale's slug stays on the catch-all.
   */
  defaultSegment?: string
}

let historyReappliesPrefix = false
let activeLocaleHistory: LocaleHistory | undefined
const localeSegmentListeners = new Set<() => void>()

/**
 * Whether the installed history puts the locale prefix back on navigation.
 *
 * The two ways of localizing routes need opposite things from a caller holding a
 * locale-free target. Under `withLocaleRoutes` the prefix is part of the matched
 * path, so a caller has to supply it. Under `createLocaleHistory` the history
 * supplies it, and a caller that also does sends `/de/de/video`.
 *
 * Module state rather than a provider because it describes how the router was
 * constructed, and the one package that could host a provider
 * (`@repo/studio-config`) already depends on this one. It is set at boot by the
 * single `createLocaleHistory` call an app makes, and never unset.
 */
export function historyReappliesLocalePrefix(): boolean {
  return historyReappliesPrefix
}

/**
 * Subscribe to the active locale history's segment. `setLocaleSegment` only
 * replaceState's the address bar — the router location never changes — so
 * `<Link>` has to hear about the new segment some other way or its href stays
 * on the previous locale.
 */
export function subscribeLocaleHistorySegment(onStoreChange: () => void): () => void {
  localeSegmentListeners.add(onStoreChange)

  return () => {
    localeSegmentListeners.delete(onStoreChange)
  }
}

export function getLocaleHistorySegmentSnapshot(): string | undefined {
  return activeLocaleHistory?.localeSegment
}

function notifyLocaleHistorySegment() {
  for (const listener of localeSegmentListeners) {
    listener()
  }
}

export function refreshActiveLocalizedSlug() {
  activeLocaleHistory?.refreshLocalizedSlug()
}

export function createLocaleHistory(
  base: History,
  {
    window: win = window,
    slugs = passThroughSlugs,
    defaultSegment,
  }: CreateLocaleHistoryOptions = {},
): LocaleHistory {
  historyReappliesPrefix = true

  let localeSegment = splitLocaleSegment(base.location.pathname).localeSegment

  const syncLocaleSegment = (pathname: string) => {
    const next = splitLocaleSegment(pathname).localeSegment

    if (next === localeSegment) {
      return
    }

    localeSegment = next
    notifyLocaleHistorySegment()
  }

  const prefixPathname = (pathname: string) =>
    `/${localeSegment}${pathname === '/' ? '' : pathname}`

  /**
   * Links already emit the localized slug. A cold load of the English slug
   * under a locale would otherwise leave both URLs live. Same replace as
   * `setLocaleSegment`: the router still sees the canonical path.
   */
  const showLocalizedSlug = () => {
    const url = new URL(win.location.href)
    const { localeSegment: segment, pathname: bare } = splitLocaleSegment(url.pathname)
    const tableSegment = segment ?? localeSegment ?? defaultSegment

    if (tableSegment === undefined) {
      return
    }

    const canonical = slugs.toCanonical(bare, tableSegment)
    const localized = segment === undefined ? canonical : slugs.toLocalized(canonical, segment)

    if (localized === bare) {
      return
    }

    url.pathname =
      segment === undefined ? localized : `/${segment}${localized === '/' ? '' : localized}`
    win.history.replaceState(win.history.state, '', url)
  }

  showLocalizedSlug()

  const stripPath = <T extends Path>(path: T): T => {
    const { localeSegment: segment, pathname } = splitLocaleSegment(path.pathname)
    const tableSegment = segment ?? defaultSegment

    return {
      ...path,
      pathname: tableSegment === undefined ? pathname : slugs.toCanonical(pathname, tableSegment),
    }
  }

  // A masked navigation carries two paths: `pathname` is what the router matched
  // and `mask` is what the address bar shows. Both cross this boundary, so both
  // are translated — otherwise the visible URL of a masked route loses its
  // prefix while the matched one keeps it.
  const strip = (location: Location): Location => ({
    ...stripPath(location),
    mask: location.mask && stripPath(location.mask),
  })

  let routerListener: HistoryListener | undefined
  let routerPathname = strip(base.location).pathname

  /**
   * Replays the current location to the router when its canonical path has
   * changed underneath it.
   *
   * The router caches the location it matched, and canonicalization is not a
   * fixed function of the URL: it depends on which slug tables are loaded. A
   * reader landing on a locale prefix carrying another locale's slug matches
   * nothing on the first pass, and the language sync then rewrites the prefix
   * through `setLocaleSegment`, which by design does not notify the router —
   * leaving the 404 match live on a URL the router could now resolve, until a
   * reload. Going through the router's own history listener makes it rematch
   * without touching the address bar or adding a history entry.
   */
  const rematchCanonicalPath = () => {
    const location = strip(base.location)

    if (location.pathname === routerPathname) {
      return
    }

    routerPathname = location.pathname
    routerListener?.({ action: NavigationType.Replace, location, delta: 0 })
  }

  // Only absolute pathnames are prefixed. A relative `to` resolves against the
  // current route, which the router already resolves against its own
  // locale-free location — prefixing it here would turn `details` into an
  // absolute `/de/details` and navigate somewhere else entirely.
  // A target that already carries a locale is honoured as the locale to use,
  // rather than prefixed again. Callers that canonicalize a path themselves — the
  // language sync computes `/ro` — would otherwise be sent to `/da/ro`.
  const applyPrefix = (pathname: string): string => {
    const { localeSegment: target, pathname: bare } = splitLocaleSegment(pathname)
    const segment = target ?? localeSegment

    if (segment === undefined) {
      return pathname
    }

    const canonical = slugs.toCanonical(bare, segment)
    const localized =
      target === undefined || canonical !== bare ? slugs.toLocalized(canonical, segment) : bare

    return `/${segment}${localized === '/' ? '' : localized}`
  }

  const prefix = (to: To): To => {
    const { pathname, search = '', hash = '' } = toPath(to)

    if (pathname === undefined || !pathname.startsWith('/')) {
      return to
    }

    if (typeof to === 'string') {
      return `${applyPrefix(pathname)}${search}${hash}`
    }

    const mask = 'mask' in to ? (to.mask as Path | undefined) : undefined

    return {
      ...to,
      pathname: applyPrefix(pathname),
      ...(mask ? { mask: { ...mask, pathname: applyPrefix(mask.pathname) } } : {}),
    }
  }

  const history: LocaleHistory = {
    get action() {
      return base.action
    },
    get location() {
      return strip(base.location)
    },
    get localeSegment() {
      return localeSegment
    },
    // `createHref` is the only member whose result the reader sees — it backs
    // `<Link>` hrefs, so it has to carry the prefix. `encodeLocation` and
    // `createURL` are internal normalisation the router applies to a location
    // *before* matching it, so they stay in the router's locale-free space;
    // prefixing them made every link resolve to the catch-all.
    createHref: (to: To) => base.createHref(prefix(to)),
    createURL: (to: To) => base.createURL(to),
    encodeLocation: (to: To) => base.encodeLocation(to),
    push: (to: To, state?: unknown) => {
      base.push(prefix(to), state)
      syncLocaleSegment(base.location.pathname)
      routerPathname = strip(base.location).pathname
    },
    replace: (to: To, state?: unknown) => {
      base.replace(prefix(to), state)
      syncLocaleSegment(base.location.pathname)
      routerPathname = strip(base.location).pathname
    },
    go: (delta: number) => base.go(delta),
    // A POP can land on a different locale than the one last applied — a reader
    // going back across a language switch, or arriving from a localized link.
    // Re-reading it from the URL keeps the adapter and the address bar agreed.
    listen: (listener: HistoryListener) => {
      routerListener = listener

      const unlisten = base.listen(({ action, location, delta }) => {
        syncLocaleSegment(location.pathname)
        showLocalizedSlug()

        const stripped = strip(location)
        routerPathname = stripped.pathname
        listener({ action, location: stripped, delta })
      })

      return () => {
        routerListener = undefined
        unlisten()
      }
    },
    refreshLocalizedSlug: () => {
      showLocalizedSlug()
      rematchCanonicalPath()
    },
    setLocaleSegment(segment: string | undefined) {
      if (segment !== undefined && !isRouteLocale(segment)) {
        throw new Error(`Not a shipped Studio locale segment: ${segment}`)
      }

      if (segment === localeSegment) {
        return
      }

      localeSegment = segment

      const url = new URL(win.location.href)
      const { localeSegment: previous, pathname: bare } = splitLocaleSegment(url.pathname)
      const tableSegment = previous ?? defaultSegment
      const canonical = tableSegment === undefined ? bare : slugs.toCanonical(bare, tableSegment)

      url.pathname =
        segment === undefined ? canonical : prefixPathname(slugs.toLocalized(canonical, segment))

      // Reusing the existing history state keeps react-router's own entry
      // bookkeeping (`idx`, `key`, user state) intact, so this stays invisible
      // to the router rather than desynchronising its history index.
      win.history.replaceState(win.history.state, '', url)
      notifyLocaleHistorySegment()
      rematchCanonicalPath()
    },
  }

  activeLocaleHistory = history

  return history
}
